import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, User, MapPin, Phone, Mail, Bell, Copy, Check,
  Truck, Clock, CheckCircle, AlertCircle, LogOut, Plus,
  Home, FileText, Receipt, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { MiExperiencia } from '../components/cliente/MiExperiencia';
import { ResumenFidelidad } from '../components/cliente/ResumenFidelidad';
import { MisEntregas } from '../components/cliente/MisEntregas';
import { PaqueteDetalle } from '../components/cliente/PaqueteDetalle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClienteDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [perfil, setPerfil] = useState(null);
  const [paquetes, setPaquetes] = useState({ stats: {}, paquetes: [] });
  const [facturas, setFacturas] = useState({ total: 0, facturas: [] });
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [pkgSel, setPkgSel] = useState(null);
  

  useEffect(() => {
    const token = localStorage.getItem('sendhapy_token');
    if (!token) {
      navigate('/cliente');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('sendhapy_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [perfilRes, paquetesRes, facturasRes, resumenRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cliente/perfil`, { headers }),
        axios.get(`${BACKEND_URL}/api/cliente/paquetes`, { headers }),
        axios.get(`${BACKEND_URL}/api/cliente/facturas`, { headers }),
        axios.get(`${BACKEND_URL}/api/cliente/resumen`, { headers })
      ]);
      setPerfil(perfilRes.data);
      setPaquetes(paquetesRes.data);
      setFacturas(facturasRes.data);
      setResumen(resumenRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('sendhapy_token');
        navigate('/cliente');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('sendhapy_token');
    localStorage.removeItem('sendhapy_cliente');
    navigate('/cliente');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const getEstadoBadge = (estado) => {
    const badges = {
      'en_espera': 'bg-blue-100 text-blue-700',
      'pendiente': 'bg-yellow-100 text-yellow-700',
      'en_bodega_usa': 'bg-purple-100 text-purple-700',
      'en_transito': 'bg-orange-100 text-orange-700',
      'en_aduana': 'bg-pink-100 text-pink-700',
      'en_bodega_cr': 'bg-indigo-100 text-indigo-700',
      'en_ruta': 'bg-cyan-100 text-cyan-700',
      'entregado': 'bg-green-100 text-green-700'
    };
    return badges[estado] || 'bg-gray-100 text-gray-700';
  };

  const formatEstado = (estado) => {
    const estados = {
      'en_espera': 'En espera de llegada',
      'pendiente': 'En bodega Miami',
      'en_bodega_usa': 'En bodega Miami',
      'en_transito': 'En tránsito a CR',
      'en_aduana': 'En aduana',
      'en_bodega_cr': 'Listo para entrega',
      'en_ruta': 'En ruta de entrega',
      'entregado': 'Entregado'
    };
    return estados[estado] || estado;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f5f5f5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" 
               style={{ borderColor: 'var(--sendhapy-teal)' }}></div>
          <p className="text-gray-600">Cargando tu cuenta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f5f5f5' }}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/">
            <img src="/sendhapy-logo-badge.png" alt="SendHapy" className="h-11 w-auto" data-testid="cliente-header-logo" />
          </a>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 hidden sm:block">Hola, {perfil?.nombre?.split(' ')[0]}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
            >
              <LogOut size={20} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', icon: Home, label: 'Dashboard' },
            { id: 'paquetes', icon: Package, label: 'Mis Paquetes' },
            { id: 'facturas', icon: Receipt, label: 'Mis Facturas' },
            { id: 'casillero', icon: MapPin, label: 'Mi Casillero' },
            { id: 'experiencia', icon: Sparkles, label: 'Mi Experiencia' },
            { id: 'perfil', icon: User, label: 'Mi Perfil' }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`cliente-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              style={activeTab === tab.id ? { background: 'var(--sendhapy-teal)' } : {}}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <ResumenFidelidad resumen={resumen} />
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Package size={24} style={{ color: 'var(--sendhapy-teal)' }} />
                  <span className="text-3xl font-bold">{paquetes.stats.total || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">Total Paquetes</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Clock size={24} className="text-blue-500" />
                  <span className="text-3xl font-bold">{paquetes.stats.en_espera || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">En Espera</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Package size={24} className="text-purple-500" />
                  <span className="text-3xl font-bold">{paquetes.stats.en_bodega || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">En Bodega</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <Truck size={24} className="text-orange-500" />
                  <span className="text-3xl font-bold">{paquetes.stats.en_transito || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">En Tránsito</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-listos">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin size={24} className="text-teal-600" />
                  <span className="text-3xl font-bold">{paquetes.stats.listos || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">Listos para entrega</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle size={24} className="text-green-500" />
                  <span className="text-3xl font-bold">{paquetes.stats.entregados || 0}</span>
                </div>
                <p className="text-gray-500 text-sm">Entregados</p>
              </div>
            </div>

            {/* Quick Casillero Info */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">Tu Dirección en Miami</h3>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-mono text-lg">
                  {perfil?.direccion_miami?.first_name} {perfil?.direccion_miami?.last_name}<br />
                  {perfil?.direccion_miami?.direccion}<br />
                  {perfil?.direccion_miami?.direccion2 && <>{perfil?.direccion_miami?.direccion2}<br /></>}
                  {perfil?.direccion_miami?.ciudad}, {perfil?.direccion_miami?.estado} {perfil?.direccion_miami?.zip}<br />
                  {perfil?.direccion_miami?.pais}
                </p>
                <button
                  onClick={() => copyToClipboard(`${perfil?.direccion_miami?.first_name} ${perfil?.direccion_miami?.last_name}\n${perfil?.direccion_miami?.direccion}\n${perfil?.direccion_miami?.direccion2 || ''}\n${perfil?.direccion_miami?.ciudad}, ${perfil?.direccion_miami?.estado} ${perfil?.direccion_miami?.zip}\n${perfil?.direccion_miami?.pais}`)}
                  className="mt-3 flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copiado!' : 'Copiar dirección'}
                </button>
              </div>
            </div>

            {/* Recent Packages */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
                Paquetes Recientes
              </h3>
              {paquetes.paquetes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No tienes paquetes registrados aún</p>
                  <p className="text-sm mt-2">Tus compras aparecerán aquí automáticamente cuando lleguen a nuestra bodega en Miami.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paquetes.paquetes.slice(0, 5).map((pkg, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-mono text-sm text-gray-600">{pkg.tracking}</p>
                        <p className="font-medium">{pkg.descripcion}</p>
                        {pkg.peso_kg && <p className="text-sm text-gray-500">{pkg.peso_kg} kg</p>}
                      </div>
                      <div className="text-right">
                        {pkg.monto_usd && (
                          <p className="font-bold text-green-600 mb-1">${Number(pkg.monto_usd).toFixed(2)}</p>
                        )}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEstadoBadge(pkg.estado)}`}>
                          {formatEstado(pkg.estado)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Paquetes Tab */}
        {activeTab === 'paquetes' && (
          <div className="space-y-6">

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold" style={{ color: 'var(--sendhapy-teal)' }}>
                  Mis Paquetes
                </h3>
              </div>

            {paquetes.paquetes.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package size={64} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg">No tienes paquetes registrados</p>
                <p className="text-sm mt-2">Crea una pre-alerta cuando realices una compra</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Tracking</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Descripción</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Peso</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Monto</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paquetes.paquetes.map((pkg, idx) => (
                      <tr
                        key={idx}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => setPkgSel(pkg)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setPkgSel(pkg)}
                        tabIndex={0}
                        role="button"
                        data-testid={`pkg-row-${pkg.tracking}`}
                      >
                        <td className="py-4 px-4 font-mono text-sm">{pkg.tracking}</td>
                        <td className="py-4 px-4">{pkg.descripcion}</td>
                        <td className="py-4 px-4">
                          {pkg.peso_kg ? (
                            <span data-testid={`pkg-peso-${pkg.tracking}`}>
                              <b>{pkg.peso_kg} kg</b>
                              {pkg.requiere_permiso && <span className="block text-xs text-amber-700" data-testid={`pkg-permiso-${pkg.tracking}`}>Permiso especial +${Number(pkg.recargo_permiso_usd).toFixed(2)}</span>}
                            </span>
                          ) : (
                            <span className="text-gray-400">Pendiente</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {pkg.monto_usd ? (
                            <span className="font-bold text-green-600">${Number(pkg.monto_usd).toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-400">Por calcular</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getEstadoBadge(pkg.estado)}`}>
                            {formatEstado(pkg.estado)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-sm text-gray-500 mt-4">
                  Toca un paquete para ver su detalle e historial de movimientos.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  * Hasta 500 g: $7 · 600 g: $8.40 · 700 g: $9.80 · 800 g a 1 kg: $14 · Más de 1 kg: ${paquetes.tarifa_por_kg ?? 14} por kg facturable. Paquetes con permiso especial: +$2.
                </p>
              </div>
            )}
            </div>
            <PaqueteDetalle pkg={pkgSel} onClose={() => setPkgSel(null)} estadoLabel={formatEstado} estadoClase={getEstadoBadge} />
          </div>
        )}

        {/* Facturas Tab */}
        {activeTab === 'facturas' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold mb-6" style={{ color: 'var(--sendhapy-teal)' }}>
              Mis Facturas
            </h3>

            {facturas.facturas.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Receipt size={64} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg">No tienes facturas aún</p>
                <p className="text-sm mt-2">Las facturas se generan cuando tus paquetes son procesados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Factura</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Fecha</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Paquetes</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Total USD</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Total ₡</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturas.facturas.map((factura, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="py-4 px-4 font-mono font-semibold" style={{ color: 'var(--sendhapy-teal)' }}>
                          {factura.numero}
                        </td>
                        <td className="py-4 px-4">{factura.fecha}</td>
                        <td className="py-4 px-4">{factura.paquetes_count}</td>
                        <td className="py-4 px-4 font-semibold">${factura.subtotal_usd}</td>
                        <td className="py-4 px-4">₡{factura.total_colones?.toLocaleString()}</td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            factura.estado === 'pagada' 
                              ? 'bg-green-100 text-green-700' 
                              : factura.estado === 'cancelada'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {factura.estado === 'pagada' ? 'Pagada' : factura.estado === 'cancelada' ? 'Cancelada' : 'Por pagar'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Casillero Tab */}
        {activeTab === 'casillero' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--sendhapy-teal)' }}>
                Tu Dirección en Miami
              </h3>
              
              <h4 className="font-semibold mb-3">Usa esta dirección para tus compras en USA:</h4>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">First Name:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.first_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Name:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Address Line 1:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.direccion}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Address Line 2:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.direccion2}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">City:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.ciudad}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">State:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.estado}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">ZIP Code:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.zip}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Country:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.pais}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Phone:</p>
                    <p className="font-medium">{perfil?.direccion_miami?.telefono}</p>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(
                    `First Name: ${perfil?.direccion_miami?.first_name}\nLast Name: ${perfil?.direccion_miami?.last_name}\nAddress Line 1: ${perfil?.direccion_miami?.direccion}\nAddress Line 2: ${perfil?.direccion_miami?.direccion2}\nCity: ${perfil?.direccion_miami?.ciudad}\nState: ${perfil?.direccion_miami?.estado}\nZIP Code: ${perfil?.direccion_miami?.zip}\nCountry: ${perfil?.direccion_miami?.pais}\nPhone: ${perfil?.direccion_miami?.telefono}`
                  )}
                  className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg font-medium w-full justify-center"
                  style={{ background: 'var(--sendhapy-teal)', color: 'white' }}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? '¡Copiado!' : 'Copiar dirección completa'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h4 className="font-semibold mb-3">¿Cómo usar tu casillero?</h4>
              <ol className="space-y-3 text-gray-600">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <span>Usa esta dirección cuando compres en tiendas de USA (Amazon, eBay, etc.)</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <span>Crea una pre-alerta con el número de tracking de tu compra</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <span>Te notificamos cuando llegue a Miami y cuando esté listo en Costa Rica</span>
                </li>
              </ol>
            </div>
          </div>
        )}

        {activeTab === 'facturas' && <MisEntregas />}

        {activeTab === 'experiencia' && <MiExperiencia />}

        {/* Perfil Tab */}
        {activeTab === 'perfil' && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold mb-6" style={{ color: 'var(--sendhapy-teal)' }}>
              Mi Perfil
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <User size={16} /> Nombre completo
                  </label>
                  <p className="font-medium text-lg">{perfil?.nombre}</p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Mail size={16} /> Correo electrónico
                  </label>
                  <p className="font-medium">{perfil?.email}</p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Phone size={16} /> Teléfono
                  </label>
                  <p className="font-medium">{perfil?.telefono}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <MapPin size={16} /> Dirección en Costa Rica
                  </label>
                  <p className="font-medium">{perfil?.direccion}</p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Bell size={16} /> Notificaciones
                  </label>
                  <div className="flex gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm ${perfil?.notificaciones_whatsapp ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      WhatsApp {perfil?.notificaciones_whatsapp ? '✓' : '✗'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm ${perfil?.notificaciones_email ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Email {perfil?.notificaciones_email ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t">
              <h4 className="font-semibold mb-4">¿Necesitas ayuda?</h4>
              <a
                href="https://wa.me/50688937777"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium"
                style={{ background: '#25D366', color: 'white' }}
              >
                <Phone size={20} />
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteDashboard;
